import * as React from 'react';
import {connect} from 'react-redux';
import {IconButton, WithStyles, withStyles, Dialog} from '@material-ui/core';
import TranslateNew from '@translate/TranslateNew';
import { ISchedule } from '@app-types/websocket/schedule.types';
import { schoolAgeFromFamily } from '@helpers/schoolHelper';
import SCHOOL_TYPES from '@constants/schoolTypes';
import { IFamilyMember } from '@app-types/websocket/family.types';
import { IHandoverData } from '../calendar.types';
import ArrowIcon from "@material-ui/icons/ArrowForwardIosRounded";
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import CloseIcon from '@material-ui/icons/CloseRounded';
import ThumbDownIcon from '@material-ui/icons/ThumbDown';
import DeleteIcon from '@material-ui/icons/DeleteForeverRounded';
import BackArrowIcon from "@material-ui/icons/ArrowBackIosRounded";
import { IScheduleChange, ISuggestion } from '@app-types/websocket/suggestion.types';
import { SUGGESTION_CHANGE_TYPES, SUGGESTION_TYPES } from '@constants/suggestionTypes';
import { ChangesStyle } from './changes.style';
import { DialogTop } from '@components/economy/DialogComponens';
import { calculateChangedDays } from '../editPane/ChangeTimePlaceCalc';
import DayVisitChange from '../DayVisitChange';
import { createSchedulesFromChanges, describeScheduleChanges, IChangeHandover,
  IScheduleChangeDescription } from '../changeDisplayHelper';

const tn: TranslateNew = TranslateNew.getInstance();

interface IState {
  step:number;
}

interface IProps extends WithStyles<typeof ChangesStyle> {
  open:boolean;
  close:() => void;

  userId:number;
  family:IFamilyMember[];
  schedules:ISchedule[];
  suggestion:ISuggestion|undefined;
}

interface IRenderedChange {
  element:JSX.Element;
  time:number;
}

class ShowChanges extends React.Component<IProps, IState> {

  private containerRef: React.RefObject<HTMLDivElement> = React.createRef();

  constructor(props: IProps) {
    super(props);

    this.state = {
      step:0,
    };

  }

  public componentDidUpdate(prevProps:IProps, oldState:IState) {
    if (this.props.open !== prevProps.open) {
      if (this.props.open) {
        this.setStep(0);
      }
    } else {
      if (prevProps.suggestion !== this.props.suggestion) {
        this.setStep(0);
      }  
    }
    if (oldState.step !== this.state.step) {
      const element = document.getElementById('suggchange' + (this.state.step));
      if (element !== null) {
        element.scrollIntoView({behavior:'smooth'});
      }
    }
  }

  public render() {
    const {step} = this.state;
    const {classes, suggestion, schedules, family, userId, open, close} = this.props;


    if (suggestion === undefined) {return <div/>;}
    if (suggestion.schedules.length !== 1) {return <div/>;}
    const schedule = schedules.find((s) => s.id === suggestion.schedules[0]);
    if (schedule === undefined) {return <div/>;}

    const changes:JSX.Element[] = this.generateChangesFromSuggestion(suggestion, schedule);
    if (changes.length === 0) {
      changes.push(<div className={classes.changeContainer} key={"suggchange0"} id={"suggchange0"}>{tn.lang.unknown}</div>);
    }


    const title:string = tn.lang.calSuggestionFrom(
      tn.familyMemberName(suggestion.creatorId,family), tn.formatMonthDate(new Date(suggestion.publishTime)));
    let subTitle:React.ReactNode;
    let icon:JSX.Element = <div/>;
    if (suggestion.status === SUGGESTION_TYPES.AWAITING_APPROVAL) {

      if (suggestion.creatorId === userId) {
        subTitle = tn.lang.calSuggestionAwaitReply;
      } else {
        subTitle = tn.lang.calSuggestionAwaitReplyFYou;
      }
    } else if (suggestion.status === SUGGESTION_TYPES.ACCEPTED_AND_EXECUTED) {
      subTitle = tn.lang.calSuggestionStatusAccepted(tn.familyMemberName(suggestion.responseAccountId, family),
        tn.formatMonthDate(new Date(suggestion.responseTime)));
      icon = <ThumbUpIcon fontSize='small' style={{fill:'#93A37C', padding:'12px', fontSize: '1.5rem'}} />
    } else if (suggestion.status === SUGGESTION_TYPES.DELETED || (suggestion.status === SUGGESTION_TYPES.REJECTED
      && suggestion.creatorId === suggestion.responseAccountId)) {
      icon = <DeleteIcon fontSize='small' style={{ padding:'12px', fontSize: '1.5rem'}}/>
      subTitle = tn.lang.calSuggestionStatusDeleted(tn.familyMemberName(suggestion.responseAccountId, family),
        tn.formatMonthDate(new Date(suggestion.responseTime)));
    }  else if (suggestion.status === SUGGESTION_TYPES.REJECTED) {
      icon = <ThumbDownIcon fontSize='small' style={{fill:'#D96159', padding:'12px', fontSize: '1.5rem'}} />
      subTitle = tn.lang.calSuggestionStatusRejected(tn.familyMemberName(suggestion.responseAccountId, family),
        tn.formatMonthDate(new Date(suggestion.responseTime)));
    }

    const dialogTitle = tn.lang.suggestionName(suggestion ? suggestion.id : 0 );

    return(
      <Dialog
        open={ open }
        onClose={() => close()}
        classes={{paper:this.props.classes.widePaper}}
      >
        <DialogTop>
          {icon}
          <div style={{fontWeight: 700, fontSize:'16px', flex:1}}>{dialogTitle}</div>
          <IconButton onClick={() => close()} >
            <CloseIcon />
          </IconButton>
        </DialogTop>
        <div style={{padding: '12px', gap: '24px', display: 'flex', flexDirection: 'column'}}>
          <div className={classes.paneStatus} >
            <div>{title}</div>
            <div>{subTitle}</div>
          </div>
          <div className={classes.changesContainer} ref ={this.containerRef}>
            {changes}
          </div>
          <div className={classes.paneChangeStep} >
              <IconButton onClick={() => this.setStep(step-1)} classes={{root:classes.paneChangeArrowButton}}
                disabled={step<=0} >
                <BackArrowIcon />
              </IconButton>
              <div style={{fontWeight: 700, fontSize:'16px'}}>{tn.lang.calChange((step+1),changes.length)}</div>
              <IconButton onClick={() => this.setStep(step+1)}  classes={{root:classes.paneChangeArrowButton}}
                disabled={step>=(changes.length-1)}   >
                <ArrowIcon />
              </IconButton>
            </div>
        </div>
      </Dialog>

    )
  }


  private setStep(newstep:number) {
    this.setState({step:newstep})
  }

  private generateChangesFromSuggestion(suggestion:ISuggestion, schedule:ISchedule):JSX.Element[] {
    const changes:IRenderedChange[] = [];

    const schedules = createSchedulesFromChanges(schedule, suggestion.changes);
    if (schedules === undefined) {
      changes.push({element:<div className={this.props.classes.changeContainer}>{tn.lang.unknown}</div>, time:0});
    } else {
      for (const range of calculateChangedDays(schedules.original, schedules.suggested)) {
        for (const description of describeScheduleChanges(schedules.original, schedules.suggested, range)) {
          changes.push({element:this.generateChange(description, changes.length+1), time:description.time});
        }
      }
    }

    for (const change of suggestion.changes) {
      if (change.scheduleId !== schedule.id) {continue;}
      const specialChange = this.generateSpecialChange(change, suggestion, schedule, changes.length+1);
      if (specialChange) {
        changes.push(specialChange);
      }
    }

    return this.wrapRenderedChanges(changes);
  }

  private wrapRenderedChanges(changes:IRenderedChange[]):JSX.Element[] {
    changes.sort((a,b) => a.time-b.time);
    return changes.map((change, index) => (
      <div key={"suggchange"+index} id={"suggchange"+index} style={{minWidth:'100%'}}>
        {change.element}
      </div>
    ));
  }

  private generateSpecialChange(change:IScheduleChange, suggestion:ISuggestion, schedule:ISchedule, key:number):IRenderedChange|undefined {
    if (change.type === SUGGESTION_CHANGE_TYPES.ACTIVATE_SCHEDULE) {
      return {element:<div className={this.props.classes.changeContainer}>activate</div>, time:0};
    } else if (change.type === SUGGESTION_CHANGE_TYPES.INACTIVATE_SCHEDULE) {
      return {element:<div className={this.props.classes.changeContainer}>inactivate</div>, time:0};
    } else if (change.type === SUGGESTION_CHANGE_TYPES.SHARE) {
      return {
        element:<div className={this.props.classes.changeContainer}>
          {tn.lang.scheduleShared(tn.familyMemberName(suggestion.creatorId, this.props.family))}
        </div>,
        time:0,
      };
    } else if (change.type === SUGGESTION_CHANGE_TYPES.DELETE_SCHEDULE) {
      return {
        element:<div className={this.props.classes.changeContainer}>
          {tn.lang.calEraseChange(tn.lang.scheduleNameNew(schedule))}
        </div>,
        time:0,
      };
    } else if (change.type === SUGGESTION_CHANGE_TYPES.CHANGE_CHILDREN && change.children) {
      return {
        element:<div className={this.props.classes.changeContainer}>{
          tn.lang.calKidChange(
            tn.familyMemberNames([], this.props.family),
            tn.familyMemberNames(change.children, this.props.family))}</div>,
        time:0,
      };
    } else if (change.type === SUGGESTION_CHANGE_TYPES.UNKNOWN) {
      return {element:<div className={this.props.classes.changeContainer}>{tn.lang.unknown}</div>, time:0};
    }
    return undefined;
  }

  private generateChange(description:IScheduleChangeDescription, key:number):JSX.Element {
    if (description.type === "TAKES_OVER") {
      return this.generateTakesOver(description.from, description.to, description.fromHandover, description.toHandover,
        tn.familyMemberName(description.parent, this.props.family), key);
    } else if (description.type === "DAY_VISIT") {
      return this.generateDayVisit(description.from, description.to, tn.familyMemberName(description.parent, this.props.family),
        key, description.oldFrom, description.oldTo);
    } else if (description.type === "DAY_VISIT_REMOVED") {
      return this.generateDayVisit(description.from, description.to,
        tn.familyMemberName(description.parent, this.props.family), key, undefined, undefined, true);
    } else {
      return this.generateExchangeChange(description.from, description.to,
        tn.familyMemberName(description.fromParent, this.props.family),
        tn.familyMemberName(description.toParent, this.props.family), key);
    }
  }

  private getDay(time:number):string {
    const day:Date = new Date(time);
    return tn.formatDateMonthShort(day);
  }

  private getSchool():SCHOOL_TYPES|undefined {
    const suggestion = this.props.suggestion;
    const schedule = (suggestion !== undefined && suggestion.schedules.length === 1)
      ?this.props.schedules.find((s) => s.id === suggestion.schedules[0])
      :undefined;
    return schedule?schedule.school:undefined;
  }

  private getLocation(handover:IHandoverData|null):string {
    if (handover === null) {
      return tn.lang.unknown;
    } else if (handover.atSchool) {
      return tn.lang.schedulePickupSchool(this.getSchool(), schoolAgeFromFamily(this.props.family));
    } else {
      return tn.lang.handoverTimeAndPlace(new Date(handover.time), handover.location);
    }
  }

  private generateTakesOver(from:number, to:number, fromLoc:IChangeHandover,
    toLoc:IChangeHandover, parent:string, key:number):JSX.Element {
    return (
      <div className={this.props.classes.changeInfoBox} key={"change"+key}>
        <div className={this.props.classes.changeInfoHeader}>
          <span style={{fontWeight:600, marginRight: '5px'}}>{parent}</span>{tn.lang.calTakesResponsibility}
        </div>
        <div className={this.props.classes.changeInfoRow} style={{marginBottom:'20px'}}>
          <div className={this.props.classes.changeInfoColumn}>
            <div className={this.props.classes.changeInfoLabel}>{tn.lang.from}</div>
            <div className={this.props.classes.changeInfoValue}>{this.getDay(from)}</div>
          </div>
          <div className={this.props.classes.changeInfoColumn}>
            <div className={this.props.classes.changeInfoLabel}>{tn.lang.to}</div>
            <div className={this.props.classes.changeInfoValue}>{this.getDay(to)}</div>
          </div>
        </div>
        <div className={this.props.classes.changeInfoRow}>
          {fromLoc==="CONTINUE"?"":(tn.lang.handoverExplain1+this.getDay(from)+tn.lang.handoverExplain2+this.getLocation(fromLoc))}
        </div>
        <div className={this.props.classes.changeInfoRow}>
          {toLoc==="CONTINUE"?"":(tn.lang.handoverExplain1+this.getDay(to)+tn.lang.handoverExplain2+this.getLocation(toLoc))}
        </div>
      </div>);
  }

  private generateDayVisit(from:IHandoverData, to:IHandoverData, parent:string, key:number,
    oldFrom?:IHandoverData, oldTo?:IHandoverData, removed?:boolean):JSX.Element {
    return (
      <DayVisitChange key={"change"+key} boxClassName={this.props.classes.changeInfoBox} parent={parent}
        from={from} to={to} oldFrom={oldFrom} oldTo={oldTo} removed={removed}
        schoolName={tn.lang.calDayVisitSchool(this.getSchool(), schoolAgeFromFamily(this.props.family))}/>);
  }

  private generateExchangeChange(from:IHandoverData, to:IHandoverData, fromParent:string, toParent:string,
    key:number):JSX.Element {
    return (
      <div className={this.props.classes.changeInfoBox} key={"change"+key}>
        <div className={this.props.classes.changeInfoCaption}>
          {tn.lang.calHandoverChangeWas(fromParent, toParent)}
        </div>
        <div className={this.props.classes.changeInfoRow} style={{marginBottom:'20px'}}>
          <div className={this.props.classes.changeInfoValue} style={{textDecoration:'line-through', opacity:0.5}}>
            {tn.lang.calHandoverChangeTime(new Date(from.time), this.getLocation(from))}
          </div>
        </div>
        <div className={this.props.classes.changeInfoCaption}>
          {tn.lang.calHandoverChangeTo}
        </div>
        <div className={this.props.classes.changeInfoRow}>
          <div className={this.props.classes.changeInfoValue} style={{fontWeight:700}}>
            {tn.lang.calHandoverChangeTime(new Date(to.time), this.getLocation(to))}
          </div>
        </div>
      </div>);
  }




}



function mapStateToProps(state: any) {
  const userId = state.user.session.data.id;

  return {
    userId,
    family:state.family.family,
    schedules:state.schedule.schedules?state.schedule.schedules:[],
  };
}

export default connect(mapStateToProps)(withStyles(ChangesStyle)(ShowChanges));



// WEBPACK FOOTER //
// ./src/components/calendar2/suggestion/ShowChanges.tsx